import { useIsMutating } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, matchPath } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";
import { useTenants } from "../lib/queries/tenants";
import { tenantScope } from "../lib/tenantScope";
import { useConfirmUnsavedAction } from "../lib/unsavedChanges/UnsavedChangesContext";

export function TenantScopeSwitcher({
  initialSelectionDestination = "home",
}: {
  initialSelectionDestination?: "home" | "current";
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const id = useId();
  const { user, memberships = [], switchTenant, loading } = useAuth();
  const isSuper = user?.role === "super_admin";
  const all = useTenants(isSuper);
  const mutating = useIsMutating();
  const { confirmAction, allowNavigation } = useConfirmUnsavedAction();
  const [busy, setBusy] = useState(false);
  const options = isSuper
    ? (all.data?.items ?? []).map((c) => ({ id: c.id, name: c.name, status: "active" }))
    : memberships.map((m) => ({ id: m.tenant_id, name: m.tenant_name, status: m.status }));
  const change = (value: string) =>
    confirmAction(() => {
      const keepCurrent =
        (initialSelectionDestination === "current" ||
          !!matchPath("/projects/:id", location.pathname) ||
          !!matchPath("/quote-requests/:id/preparer", location.pathname) ||
          !!matchPath("/quote-requests/:id/parcours", location.pathname)) &&
        tenantScope() === null &&
        !!value;
      if (!switchTenant(value || null)) {
        setBusy(true);
        return;
      }
      setBusy(false);
      allowNavigation();
      navigate(
        keepCurrent
          ? location.pathname + location.search + location.hash
          : location.pathname === "/settings/team"
            ? "/settings/team"
            : "/",
        { replace: true },
      );
    });
  return (
    <div className="min-w-0 max-w-full">
      <label htmlFor={id} className="block text-xs font-medium text-slate-600">
        {t("tenant_scope.label")}
      </label>
      <select
        id={id}
        value={tenantScope() ?? ""}
        onChange={(e) => change(e.target.value)}
        disabled={loading || mutating > 0}
        className="focus-visible:ring-brand-500 min-h-11 w-full max-w-64 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-800 focus-visible:outline-none focus-visible:ring-2"
      >
        <option value="" disabled={!isSuper}>
          {t("tenant_scope.none")}
        </option>
        {options.map((option) => (
          <option key={option.id} value={option.id} disabled={option.status === "disabled"}>
            {option.name}
            {option.status !== "active" ? ` — ${t(`membership.status.${option.status}`)}` : ""}
          </option>
        ))}
      </select>
      {(busy || mutating > 0) && (
        <p role="status" className="max-w-64 text-xs text-amber-800">
          {t("membership.wait_write")}
        </p>
      )}
      {isSuper && all.isError && (
        <p role="alert" className="text-xs text-red-700">
          {t("tenant_scope.load_failed")}
        </p>
      )}
    </div>
  );
}
